// Custom hook for handling message submission
import React from 'react';
import { Model, BillingSource, UserTeamSummary } from '../../types';
import { MessageWithCitations, StoredSuggestions, Conversation, AgentStep } from './chatTypes';
import { storeFile, retrieveFile } from './fileDatabase';
import { sanitizeFilename, isImageGenerationModel } from './chatUtils';
import { uploadFile, FileWorkspaceError, UnauthorizedError } from './fileWorkspaceService';
import config from '../../config';
import { setFileSelection } from './fileWorkspaceService';
import mixpanel from 'mixpanel-browser';

const BASE_URL = config.apiUrl;

// What to show for a 429 (credit refusal).
//
// TEAM refusals (`code: 'team_pool_empty'`, 'team_member_limit_reached') carry
// the sentence already written, in Indonesian, naming the team and asking the
// owner to perpanjang the plan or top up. It is relayed VERBATIM: the extra
// fields the payload now carries (allowance_remaining, plan_active) are
// pass-through, and re-assembling the text told a team member to "upgrade your
// plan", which is both English and something they cannot do.
//
// The PERSONAL refusal keeps its existing sentence on purpose: the renderer
// turns the "Please upgrade your plan to get more credits" phrase into the
// /subscription link, and that CTA is the whole point of the message.
const creditLimitMessage = (detail: any): string => {
  const code = typeof detail?.code === 'string' ? detail.code : '';
  if (code.startsWith('team_') && typeof detail?.message === 'string' && detail.message.trim()) {
    return `⚠️ ${detail.message}`;
  }
  if (detail) {
    return `⚠️ You have ${detail.remaining_credits} credits remaining. This model requires ${detail.model_cost} credits. Please upgrade your plan to get more credits`;
  }
  return '⚠️ Credit limit exceeded. Please upgrade your plan to continue';
};

export interface MessageSubmitDependencies {
  // State variables
  selectedModel: Model;
  inputMessage: string;
  isLoading: boolean;
  selectedFile: File | null;
  selectedFileName: string | null;
  selectedFiles: { file: File; filename: string }[];
  messages: MessageWithCitations[];
  currentConversationId: number | null;
  conversations: Conversation[];
  userInfo: {
    name: string;
    email: string;
    status: string;
    credits_used: number;
    subscription_type?: string;
    // Team-aware credit fields from /auth/credit-info (see src/types.ts User).
    credits_total?: number | null;
    billing_source?: BillingSource;
    team?: UserTeamSummary | null;
    raw_status?: string;
    has_team?: boolean;
  } | null;

  // Teams phase 3: the project a brand-new chat belongs to, set when the user
  // arrives from a project panel ("Chat baru di project ini"). Only the FIRST
  // message of a conversation uses it - it rides along on the create call and
  // is then cleared via clearPendingProject(). Absent/null = today's behaviour.
  pendingProjectId?: number | null;

  // Refs
  abortControllerRef: React.MutableRefObject<AbortController | null>;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  chatContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  // Live pointer to the conversation currently DISPLAYED (kept in sync by
  // Chat.tsx on every render + eagerly on navigation). The stream consumer
  // gates every UI write on it so a run belonging to conversation A can never
  // paint into conversation B / a fresh "New chat" view. Server runs are
  // per-conversation and parallel; the tab only CONSUMES the visible one.
  currentConversationIdRef: React.MutableRefObject<number | null>;

  // Helper functions
  getModelDisplayName: (model: string) => string;
  resetTextAreaHeight: () => void;

  // External functions
  navigate: (path: string) => void;
  fetchConversationMessages: (
    params: any,
    token: string,
    tokenType: string,
    callbacks: any,
    chatContainerRef?: React.RefObject<HTMLDivElement>
  ) => Promise<any>;

  // Teams phase 2: re-fetch the "Tim" sidebar block (GET
  // /teams/shared-conversations). Called at the two moments the personal
  // conversations list already moves - a run STARTS and a run FINISHES - so a
  // shared row can never keep the "Sedang berjalan…" spinner from a snapshot
  // taken mid-run. Fire-and-forget: the implementation swallows its own
  // failures (404 no_team, backend without the endpoint). Optional so callers
  // that do not render a sidebar can omit it.
  refreshSharedConversations?: () => void;

  // Re-fetch the PERSONAL conversations list (GET /conversations). Called on
  // the same run-done beat as refreshSharedConversations so the sidebar's
  // relative times ("Baru saja") and titles move with the answer instead of
  // waiting for a poll that only runs while something is in flight.
  refreshConversations?: () => void;

  // The open conversation's meta (visibility, project, title) may have been
  // decided SERVER-side during the turn: a brand new chat has no detail fetch
  // behind it at all, and one born inside a team project comes back already
  // shared. Called with the conversation the run belonged to once its messages
  // have been refetched, so the share pill and the project pill are live
  // without a reload. Fire-and-forget; the implementation swallows failures.
  onConversationMetaChanged?: (conversationId: number, conversationUuid: string) => void;
}

export interface MessageSubmitCallbacks {
  setSelectedModel: (model: Model) => void;
  setInputMessage: (message: string) => void;
  setIsLoading: (loading: boolean) => void;
  setSelectedFile: (file: File | null) => void;
  setSelectedFileName: (name: string | null) => void;
  setSelectedFiles: (files: { file: File; filename: string }[]) => void;
  setMessages: React.Dispatch<React.SetStateAction<MessageWithCitations[]>>;
  setCurrentConversationId: (id: number | null) => void;
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  setUserInfo: (info: any) => void;
  setIsFirstMessageLoading: (loading: boolean) => void;
  setShouldScroll: (should: boolean) => void;
  setShowScrollToBottom: (show: boolean) => void;
  // Inline composer error for workspace upload failures (403/413/network).
  // Rendered in the composer area; cleared at the start of each send.
  setUploadError: (message: string | null) => void;
  // Teams phase 3: called once the pending project has been attached to a
  // freshly created conversation, so the next new chat starts unattached.
  clearPendingProject?: () => void;

  // Called the moment the FIRST message creates a conversation, with what the
  // create endpoint echoed back. A chat born inside a team project comes back
  // already shared, so this is the only way the share and project pills can be
  // right before the turn even finishes. Fields absent = a plain private chat.
  onConversationCreated?: (created: {
    id: number;
    uuid: string;
    project_id?: number | null;
    visibility?: 'private' | 'team';
  }) => void;
}

// Options accepted by consumeRunStream (the public re-attachable consumer).
export interface ConsumeRunStreamOptions {
  // true when ATTACHING to an already-running/replaying run (fresh POST is
  // false). A replayed run delivers its whole history instantly, so the
  // client-side TPS estimate is meaningless - we suppress the tps display
  // when this is true.
  isReattach: boolean;
  conversationId: number | null;
  conversationUuid?: string | null;
  modelToUse?: Model;
  isFirstMessage?: boolean;
  navigationData?: { id: number; uuid: string } | null;
  startTime?: number;
  // Client id of the in-progress assistant placeholder this run writes into.
  // The consumer targets it BY ID (not by array index) and re-appends it if a
  // concurrent messages replacement (duplicate init effect, StrictMode
  // double-mount, conversation-switch refetch) wiped it. Without this the
  // consumer falls back to the legacy last-index latch, which can corrupt
  // whatever message ends up at that index after a replacement.
  assistantMessageId?: number;
}

// Fully-resolved options handed to the low-level drainStream consumer.
interface DrainStreamOptions {
  isReattach: boolean;
  conversationId: number | null;
  conversationUuid?: string | null;
  modelToUse: Model;
  isFirstMessage: boolean;
  navigationData: { id: number; uuid: string } | null;
  startTime: number;
  assistantMessageId?: number;
}

export function useMessageSubmit(
  dependencies: MessageSubmitDependencies,
  callbacks: MessageSubmitCallbacks
) {
  // Module-level variable for navigation data
  let navigationData: { id: number; uuid: string } | null = null;

  // -------------------------------------------------------------------------
  // Query the currently-active run for a conversation (used for the 409
  // fallback in the submit path). Returns a run_id only when a run is still
  // 'running'; otherwise null (finished/none → caller reconciles by refetch).
  // -------------------------------------------------------------------------
  const queryActiveRunId = async (conversationId: number | null): Promise<string | null> => {
    if (conversationId == null) return null;
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    try {
      const resp = await fetch(`${BASE_URL}/ai/conversations/${conversationId}/active-run`, {
        headers: { 'Authorization': `${tokenType} ${token}` }
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      return data && data.run_id && data.status === 'running' ? data.run_id : null;
    } catch {
      return null;
    }
  };

  // -------------------------------------------------------------------------
  // drainStream: the single SSE consumer.
  //
  // Given an already-open SSE `Response` (either the run-event stream from
  // /ai/runs/{id}/stream OR the file-analysis stream from
  // /ai/analyze/file/stream), it runs the reader loop feeding the SAME
  // handleData/applyParsed logic used before this refactor (steps upsert,
  // text deltas, retract, suggestions, usage), then runs the done-branch
  // (final commit + fetchCreditInfo + refetch(reattachTrace) + first-message
  // navigation refetch). It owns ALL per-run accumulator state; everything
  // reactive is received via `opts` or closed over from the hook deps.
  //
  // For the run stream the terminator is a `run_status` event; for the file
  // stream it's the natural close. Comment (`:` keepalive) lines are ignored.
  // status 'error' is routed to the existing stream-error UI path.
  //
  // The caller is responsible for opening the fetch (so the AbortController is
  // bound to the correct request); drainStream only clears the ref in finally.
  // -------------------------------------------------------------------------
  const drainStream = async (response: Response, opts: DrainStreamOptions): Promise<void> => {
    const { setMessages, setIsLoading, setUserInfo, setShowScrollToBottom, setConversations } = callbacks;
    const { abortControllerRef, chatContainerRef, conversations, currentConversationIdRef, fetchConversationMessages, navigate, onConversationMetaChanged, refreshConversations, refreshSharedConversations, userInfo } = dependencies;

    // True while the conversation this run belongs to is the one on screen.
    // Every UI write below is gated on it; when it flips false (user opened
    // another conversation or "New chat"), the local reader detaches and the
    // server run continues alone - reopening the conversation reattaches.
    const isViewCurrent = (): boolean => {
      if (!currentConversationIdRef) return true; // no ref wired (tests) - legacy behavior
      return opts.conversationId == null || currentConversationIdRef.current === opts.conversationId;
    };

    // The controller whose signal this stream was opened with. finally must
    // only null the shared ref if it still points at US - a consumer for the
    // newly-opened conversation may have installed its own controller already.
    const myController = abortControllerRef.current;

    const modelToUse = opts.modelToUse;
    // Suppress TPS stamping on a re-attached (replayed) stream - a replay
    // delivers the whole answer instantly so the chars/elapsed estimate is
    // nonsense. Freshly-fired runs keep the estimate.
    const stampTps = !opts.isReattach;

    let accumulatedContent = '';
    let currentMessageIndex = -1;
    const sourcesSet = new Set<string>();

    // --- TPS metering (client-side estimate, display only) ---
    let streamStartMs = 0;
    let streamTps = 0;
    const computeTps = (): number => {
      if (streamStartMs === 0) return 0;
      const elapsedSec = (performance.now() - streamStartMs) / 1000;
      if (elapsedSec <= 0.5) return streamTps; // keep last stable value early on
      return Math.round((accumulatedContent.length / 4) / elapsedSec);
    };

    // --- Research trace persistence (mirrored outside React state so it
    // survives the post-stream refetch, exactly as before this refactor) ---
    let stepsAccum: AgentStep[] = [];
    let traceStartMs = 0;
    let traceDurationMs = 0;
    let suggestionsAccum: string[] = [];
    let creditsChargedAccum: number | undefined;
    let modelsUsedAccum: { model: string; in_tokens: number; out_tokens: number; calls: number; cost_usd: number }[] = [];
    // Memori transparency: contents saved by the backend's save_memory tool
    // this turn, rendered as a "Disimpan ke memori" chip under the answer.
    let memorySavedAccum: string[] = [];

    // Re-attach the just-finished trace (+ suggestions + usage) onto the last
    // assistant message of a refetched list.
    const reattachTrace = (msgs: MessageWithCitations[]): MessageWithCitations[] => {
      if (stepsAccum.length === 0 && !(stampTps && streamTps > 0) && suggestionsAccum.length === 0
          && creditsChargedAccum === undefined && modelsUsedAccum.length === 0
          && memorySavedAccum.length === 0) return msgs;
      const out = [...msgs];
      for (let k = out.length - 1; k >= 0; k--) {
        if (out[k].role === 'assistant') {
          out[k] = {
            ...out[k],
            ...(stepsAccum.length > 0 ? { steps: stepsAccum.map(s => ({ ...s })) } : {}),
            ...(stampTps && streamTps > 0 ? { tps: streamTps } : {}),
            ...(traceDurationMs > 0 ? { traceDurationMs } : {}),
            ...(suggestionsAccum.length > 0 ? { suggestions: [...suggestionsAccum] } : {}),
            ...(creditsChargedAccum !== undefined ? { creditsCharged: creditsChargedAccum } : {}),
            ...(modelsUsedAccum.length > 0 ? { modelsUsed: modelsUsedAccum.map(m => ({ ...m })) } : {}),
            ...(memorySavedAccum.length > 0 ? { memorySaved: [...memorySavedAccum] } : {}),
          };
          break;
        }
      }
      return out;
    };

    // Once the run is over (final commit done, stream detached, or errored)
    // no deferred callback may resurrect the placeholder: a pending rAF can
    // fire AFTER the done-branch refetch replaced the list (heavy markdown
    // render blocks the frame), and an unconditional re-append would leave a
    // ghost "in progress" card below the real answer.
    let sealed = false;

    // Locate the in-progress assistant message inside a setMessages functional
    // update. By-id when assistantMessageId is provided (primary path): if the
    // message vanished - a concurrent refetch/init effect replaced the list,
    // it is RE-APPENDED at the end with the current accumulated state, so a
    // wipe can never dangle the stream nor corrupt whichever message happens
    // to sit at a stale index. Legacy last-index latch kept as fallback for
    // callers that pass no id.
    const locateTarget = (prev: MessageWithCitations[]): { arr: MessageWithCitations[]; idx: number } => {
      const arr = [...prev];
      if (opts.assistantMessageId != null) {
        let idx = arr.findIndex(m => m.id === opts.assistantMessageId);
        if (idx === -1 && sealed) return { arr, idx: -1 }; // run over - never resurrect
        if (idx === -1) {
          arr.push({
            id: opts.assistantMessageId,
            role: 'assistant',
            content: accumulatedContent,
            model: modelToUse,
            isComplete: false,
            created_at: new Date().toISOString(),
            ...(stepsAccum.length > 0 ? { steps: stepsAccum.map(s => ({ ...s })) } : {}),
            ...(suggestionsAccum.length > 0 ? { suggestions: [...suggestionsAccum] } : {}),
            ...(sourcesSet.size > 0 ? { sources: Array.from(sourcesSet) } : {}),
          });
          idx = arr.length - 1;
        }
        return { arr, idx };
      }
      if (currentMessageIndex === -1) currentMessageIndex = arr.length - 1;
      return { arr, idx: currentMessageIndex };
    };

    // Upsert a "step" event into the in-progress assistant message's steps[].
    // NOTE: fields are copied explicitly (no blind spread of the parsed
    // object) - new protocol fields like `items` must be added here or they
    // silently drop.
    const upsertStep = (step: { step_id: number; label?: string; detail?: string; items?: string[]; status?: 'active' | 'done' }) => {
      if (traceStartMs === 0) traceStartMs = performance.now(); // first step marks trace start
      // Reading steps carry the full domain list (up to 30) alongside detail.
      const items = Array.isArray(step.items)
        ? step.items.filter((s: any) => typeof s === 'string' && s.length > 0)
        : undefined;
      const pos0 = stepsAccum.findIndex(s => s.step_id === step.step_id);
      if (pos0 >= 0) {
        stepsAccum[pos0] = {
          ...stepsAccum[pos0],
          label: step.label ?? stepsAccum[pos0].label,
          detail: step.detail !== undefined ? step.detail : stepsAccum[pos0].detail,
          items: items !== undefined ? items : stepsAccum[pos0].items,
          status: step.status ?? stepsAccum[pos0].status,
        };
      } else {
        stepsAccum.push({
          step_id: step.step_id,
          label: step.label ?? '',
          detail: step.detail,
          ...(items !== undefined ? { items } : {}),
          status: step.status ?? 'active',
        });
      }
      if (!isViewCurrent()) return; // never paint another conversation's view
      const snapshot = stepsAccum.map(s => ({ ...s }));
      setMessages(prev => {
        const { arr, idx } = locateTarget(prev);
        if (idx >= 0 && arr[idx]) {
          arr[idx] = { ...arr[idx], steps: snapshot };
        }
        return arr;
      });
    };

    // Flush accumulated text (+ any collected sources) into the message.
    const applyContentUpdate = () => {
      requestAnimationFrame(() => {
        // rAF may fire after a navigation or after the run finished - re-check now
        if (sealed || !isViewCurrent()) return;
        streamTps = computeTps();
        setMessages(prev => {
          const { arr, idx } = locateTarget(prev);
          if (idx >= 0 && arr[idx]) {
            arr[idx] = {
              ...arr[idx],
              content: accumulatedContent,
              ...(stampTps && streamTps > 0 ? { tps: streamTps } : {}),
              ...(sourcesSet.size > 0 ? { sources: Array.from(sourcesSet) } : {}),
            };
          }
          if (chatContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
            setShowScrollToBottom(scrollHeight - scrollTop - clientHeight > 100);
          }
          return arr;
        });
      });
    };

    // Legacy fallback parser for non-strict lines (old Python-repr dicts).
    const parseLegacy = (raw: string): any | null => {
      try {
        if (modelToUse.toLowerCase().includes('claude')) {
          try {
            return JSON.parse(raw);
          } catch {
            const cleaned = raw
              .replace(/"/g, '&tandapetik')
              .replace(/'/g, '"')
              .replace(/&tandapetik/g, '\\"')
              .replace(/\n+/g, '')
              .trim();
            return cleaned ? JSON.parse(cleaned) : null;
          }
        }
        let cleanData;
        if (raw.includes("'text': \"")) {
          cleanData = raw
            .replace(/'text': /g, '"text":')
            .replace(/'sources':/g, '"sources":')
            .replace(/'citations':/g, '"citations":')
            .replace(/\n/g, '')
            .trim();
        } else {
          cleanData = raw
            .replace(/"/g, '&tandapetik')
            .replace(/'/g, '"')
            .replace(/&tandapetik/g, '\\"')
            .replace(/\n/g, '')
            .trim();
        }
        return cleanData ? JSON.parse(cleanData) : null;
      } catch {
        return null;
      }
    };

    // Apply a parsed SSE object (from either strict or legacy parse).
    const applyParsed = (parsed: any, live: boolean = true) => {
      if (!parsed || typeof parsed !== 'object') return;
      if (parsed.type === 'step') {
        upsertStep(parsed);
        return;
      }
      if (parsed.type === 'retract') {
        // Backend retracted pre-search guessing - clear what was streamed.
        accumulatedContent = '';
        if (live) applyContentUpdate();
        return;
      }
      if (parsed.type === 'title') {
        // Backend generated a smart title after the first exchange (it only
        // fires while the title still looks auto-derived, so a user rename is
        // never clobbered). Update the sidebar entry in place; runs on replay
        // too, which is idempotent.
        const t = typeof parsed.title === 'string' ? parsed.title.trim() : '';
        if (t && opts.conversationId != null) {
          setConversations(prev => prev.map(conv =>
            conv.id === opts.conversationId ? { ...conv, title: t } : conv
          ));
        }
        return;
      }
      if (parsed.type === 'suggestions') {
        const clean = Array.isArray(parsed.items)
          ? parsed.items
              .filter((s: any) => typeof s === 'string' && s.trim().length > 0)
              .slice(0, 3)
          : [];
        if (clean.length > 0) {
          suggestionsAccum = clean;
          if (!isViewCurrent()) return;
          setMessages(prev => {
            const { arr, idx } = locateTarget(prev);
            if (idx >= 0 && arr[idx] && arr[idx].role === 'assistant') {
              arr[idx] = { ...arr[idx], suggestions: clean };
            }
            return arr;
          });
        }
        return;
      }
      if (parsed.type === 'memory') {
        // Backend saved a user memory (Memori) this turn. Chip only; the
        // 'forgotten' action needs no UI beyond the model's own confirmation.
        const content = typeof parsed.content === 'string' ? parsed.content.trim() : '';
        if (parsed.action === 'saved' && content) {
          memorySavedAccum = [...memorySavedAccum, content];
          if (!isViewCurrent()) return;
          const snapshot = [...memorySavedAccum];
          setMessages(prev => {
            const { arr, idx } = locateTarget(prev);
            if (idx >= 0 && arr[idx] && arr[idx].role === 'assistant') {
              arr[idx] = { ...arr[idx], memorySaved: snapshot };
            }
            return arr;
          });
        }
        return;
      }
      if (parsed.type === 'usage') {
        const credits = parsed.credits;
        const validCredits = typeof credits === 'number' && Number.isInteger(credits) && credits > 0;
        const models = Array.isArray(parsed.models)
          ? parsed.models
              .filter((m: any) => m && typeof m === 'object' && typeof m.model === 'string' && m.model.length > 0)
              .map((m: any) => ({
                model: m.model,
                in_tokens: typeof m.in_tokens === 'number' ? m.in_tokens : 0,
                out_tokens: typeof m.out_tokens === 'number' ? m.out_tokens : 0,
                calls: typeof m.calls === 'number' ? m.calls : 0,
                cost_usd: typeof m.cost_usd === 'number' ? m.cost_usd : 0,
              }))
          : [];
        if (validCredits) creditsChargedAccum = credits;
        if (models.length > 0) modelsUsedAccum = models;
        if ((validCredits || models.length > 0) && isViewCurrent()) {
          setMessages(prev => {
            const { arr, idx } = locateTarget(prev);
            if (idx >= 0 && arr[idx] && arr[idx].role === 'assistant') {
              arr[idx] = {
                ...arr[idx],
                ...(validCredits ? { creditsCharged: credits } : {}),
                ...(models.length > 0 ? { modelsUsed: models } : {}),
              };
            }
            return arr;
          });
        }
        return;
      }
      let changed = false;
      if (typeof parsed.text === 'string' && parsed.text) {
        if (streamStartMs === 0) streamStartMs = performance.now(); // first text delta
        accumulatedContent += parsed.text;
        changed = true;
      }
      if (Array.isArray(parsed.sources)) {
        parsed.sources.forEach((s: string) => { if (s) sourcesSet.add(s); });
        changed = true;
      }
      if (changed && live) applyContentUpdate();
    };

    // Parse one "data: ..." payload. Returns a run terminator signal
    // ('done'|'error') when the line is a run_status control event, otherwise
    // applies the event and returns null.
    const handleData = (raw: string, live: boolean = true): 'done' | 'error' | null => {
      if (raw.trim() === '[DONE]') return null;
      let parsed: any = null;
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = parseLegacy(raw);
      }
      if (!parsed || typeof parsed !== 'object') return null;
      if (parsed.type === 'run_status') {
        return parsed.status === 'error' ? 'error' : 'done';
      }
      applyParsed(parsed, live);
      return null;
    };

    try {
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');

      let buffer = '';
      let runStatus: 'done' | 'error' | null = null;
      let detached = false;

      while (true) {
        const { done, value } = await reader.read();

        // User navigated to another conversation / "New chat" while we were
        // reading: DETACH the local reader (server run continues; the events
        // live in the DB and reopening the conversation replays everything).
        // Checked before processing so no event of a background run ever
        // reaches this view, including the run_status terminator (its
        // done-branch must not refetch into the wrong view).
        if (!isViewCurrent()) {
          try { await reader.cancel(); } catch { /* already closed */ }
          detached = true;
          break;
        }

        if (done) break; // natural close (file stream terminates this way)

        buffer += new TextDecoder().decode(value);
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // keep last incomplete line in buffer

        for (const line of lines) {
          if (line.trim() === '' || line.startsWith(':')) continue; // skip blanks + ": keepalive" comments
          if (!line.startsWith('data: ')) continue;
          const sig = handleData(line.slice(6));
          if (sig) { runStatus = sig; break; }
        }
        if (runStatus) break;
      }

      if (detached) {
        sealed = true; // stop any pending rAF from writing after detach
        console.log(`Run stream for conversation ${opts.conversationId} detached (view changed) - server run continues`);
        return; // no done-branch, no error UI; finally handles ref cleanup
      }

      // Drain any remaining buffered line (accumulate only; final commit follows).
      if (!runStatus && buffer.trim()) {
        for (const line of buffer.split('\n')) {
          if (line.startsWith(':') || !line.startsWith('data: ')) continue;
          const sig = handleData(line.slice(6), false);
          if (sig) runStatus = sig;
        }
      }

      // TERMINAL: the server has spoken, whichever way it went. Drop the
      // reattach pointer HERE, before the error branch throws - it used to be
      // cleared only on the happy path, so a failed run left an
      // `activeRun:{id}` key behind that hijacked a later bare /c and pulled
      // the user back into that conversation instead of a new chat.
      if (runStatus && opts.conversationId != null) {
        localStorage.removeItem(`activeRun:${opts.conversationId}`);
      }

      // A run that ended in error is routed to the existing stream-error path.
      if (runStatus === 'error') {
        throw new Error('The response ended unexpectedly. Please try again.');
      }

      // --- Final commit: mark the in-progress assistant message complete ---
      streamTps = computeTps();
      traceDurationMs = traceStartMs > 0 ? Math.round(performance.now() - traceStartMs) : 0;
      if (isViewCurrent()) setMessages(prev => {
        const { arr: newMessages, idx: located } = locateTarget(prev);
        let idx = located;
        if (idx < 0) {
          const last = newMessages.length - 1;
          if (last >= 0 && newMessages[last].role === 'assistant') idx = last;
        }
        if (idx >= 0 && newMessages[idx]) {
          newMessages[idx] = {
            ...newMessages[idx],
            content: accumulatedContent,
            isComplete: true,
            ...(stampTps && streamTps > 0 ? { tps: streamTps } : {}),
            ...(stepsAccum.length > 0 ? { steps: stepsAccum.map(s => ({ ...s })) } : {}),
            ...(traceDurationMs > 0 ? { traceDurationMs } : {}),
            ...(suggestionsAccum.length > 0 ? { suggestions: [...suggestionsAccum] } : {}),
            ...(creditsChargedAccum !== undefined ? { creditsCharged: creditsChargedAccum } : {}),
            ...(modelsUsedAccum.length > 0 ? { modelsUsed: modelsUsedAccum.map(m => ({ ...m })) } : {}),
            ...(sourcesSet.size > 0 ? { sources: Array.from(sourcesSet) } : {}),
          };
        }
        return newMessages;
      });

      // The commit above was the last legitimate write for this run. Seal so a
      // late rAF (delayed by the heavy answer render) can't resurrect the
      // placeholder after the refetch below replaces the message list.
      sealed = true;

      // Run finished - clear the sidebar's optimistic "running" stamp (the
      // conversation is on screen, so no "unseen result" badge is due; the
      // transition effect in Chat.tsx marks it seen because it is the open
      // conversation). The reattach pointer was already dropped on the terminal
      // signal above, which also covers the error path.
      if (opts.conversationId != null) {
        setConversations(prev => prev.map(c =>
          c.id === opts.conversationId ? { ...c, active_run_status: null } : c
        ));
      }

      // Same moment, same treatment for the "Tim" block: a shared conversation
      // gets its running stamp from a server list, not from the optimistic
      // update above, so it needs a fresh fetch here or it would keep spinning
      // until some later poll happened to run. Silent by contract.
      refreshSharedConversations?.();
      // The personal list moves on the same beat: its rows carry the relative
      // time ("Baru saja") and the server-side title, and the optimistic stamp
      // above only clears the spinner. Without this the row kept showing the
      // time it had when the turn started until some later poll ran.
      refreshConversations?.();

      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType');

      // Fetch credit info after response is complete
      try {
        const creditResp = await fetch(`${BASE_URL}/auth/credit-info`, {
          headers: { 'Authorization': `${tokenType} ${token}` }
        });
        if (creditResp.ok) {
          setUserInfo(await creditResp.json());
        }
      } catch (error) {
        console.error('Error fetching credit info:', error);
      }

      const refetchCallbacks = {
        onStart: () => {},
        onFinish: () => {},
        onError: (error: any) => {
          console.error('Error fetching conversation messages:', error);
        },
        onUnauthorized: () => {
          navigate('/login');
        }
      };

      // First-message navigation refetch (re-attach the finished trace card).
      // View-gated: the user may have navigated away during the awaits above,
      // a background run's refetch must never replace another view's messages.
      if (opts.isFirstMessage && opts.navigationData && isViewCurrent()) {
        const params = {
          conversationId: opts.navigationData.id,
          conversationUuid: opts.navigationData.uuid,
          cursor: null,
          direction: 'next' as const,
          pageSize: 50,
          shouldAutoScroll: false
        };
        const result = await fetchConversationMessages(params, token!, tokenType!, refetchCallbacks, chatContainerRef);
        if (result && isViewCurrent()) {
          setMessages(reattachTrace(result.messages));
        }
        // The conversation was CREATED by this turn, so nothing has ever read
        // its detail: hydrate visibility + project now, or the share and
        // project pills would sit on their defaults until a reload. This is
        // the same hand-off the move-to-project flow does with the PATCH
        // response.
        if (isViewCurrent()) {
          onConversationMetaChanged?.(
            opts.navigationData.id, opts.navigationData.uuid
          );
        }
      }

      // Conversation refetch (re-attach the finished trace card). For a first
      // message we intentionally pass no conversationUuid so this falls back to
      // the (stale) conversations list and skips - the branch above already did
      // the refetch, mirroring pre-refactor behaviour.
      if (opts.conversationId && isViewCurrent()) {
        const convUuid = opts.conversationUuid ?? conversations.find(c => c.id === opts.conversationId)?.uuid;
        if (convUuid) {
          const params = {
            conversationId: opts.conversationId,
            conversationUuid: convUuid,
            cursor: null,
            direction: 'next' as const,
            pageSize: 50,
            shouldAutoScroll: false
          };
          const result = await fetchConversationMessages(params, token!, tokenType!, refetchCallbacks, chatContainerRef);
          if (result && isViewCurrent()) {
            setMessages(reattachTrace(result.messages));
          }
          // Re-read the meta on every finished turn, not just the first: the
          // title the server generated lands here too, and a conversation can
          // have been moved or shared from another tab mid-run.
          if (isViewCurrent()) {
            onConversationMetaChanged?.(opts.conversationId, convUuid);
          }
        }
      }

      // After successful response:
      mixpanel.track('Message Received', {
        model: modelToUse,
        response_length: accumulatedContent.length,
        conversation_id: opts.conversationId,
        processing_time_ms: Date.now() - opts.startTime,
        user_email: userInfo?.email,
        user_status: userInfo?.status
      });
      mixpanel.people.increment('Total Messages Received');
      mixpanel.people.increment('Total Conversation Turns');
    } catch (error) {
      sealed = true; // no deferred write may follow an aborted/errored stream
      if (error instanceof Error && error.name === 'AbortError') {
        // Local reader aborted (e.g. user left the conversation). The server run
        // keeps going; returning re-attaches on the next open. No error UI.
        console.log('Run stream reader aborted');
        return;
      }
      const errorMessage = error instanceof Error
        ? `Error: ${error.message}`
        : 'An error occurred while processing your request.';
      if (!isViewCurrent()) {
        // A background run's failure must not inject an error bubble into
        // whatever conversation is on screen. Reopening its conversation
        // reconciles via active-run/refetch.
        console.error(`Background run stream error (conversation ${opts.conversationId}, suppressed):`, errorMessage);
        return;
      }
      setMessages(prev => [
        ...prev.slice(0, -1),
        {
          role: 'assistant',
          content: `⚠️ ${errorMessage}`,
          model: modelToUse,
          isComplete: true,
          error: true,
          id: 0,
          created_at: new Date().toISOString()
        }
      ]);
    } finally {
      // isLoading belongs to the VISIBLE view. A consumer detaching for a
      // background conversation must not clear (or race) the spinner of the
      // run the user is actually looking at.
      if (isViewCurrent()) setIsLoading(false);
      // Only release the shared abort ref if it still points at our controller
      // - the newly-viewed conversation's consumer may already own it.
      if (abortControllerRef.current === myController) {
        abortControllerRef.current = null;
      }
    }
  };

  // -------------------------------------------------------------------------
  // consumeRunStream: open the run event stream for `runId` and drain it.
  // Owns a fresh AbortController (the stream is its own request), then hands
  // the open Response to drainStream. Callable both from the submit path
  // (isReattach:false) and from Chat.tsx on conversation open (isReattach:true).
  // -------------------------------------------------------------------------
  const consumeRunStream = async (runId: string, options: ConsumeRunStreamOptions): Promise<void> => {
    const { setIsLoading, setMessages } = callbacks;
    const { abortControllerRef, currentConversationIdRef, navigate } = dependencies;

    const opts: DrainStreamOptions = {
      isReattach: options.isReattach,
      conversationId: options.conversationId,
      conversationUuid: options.conversationUuid,
      modelToUse: options.modelToUse ?? ('auto' as Model),
      isFirstMessage: options.isFirstMessage ?? false,
      navigationData: options.navigationData ?? null,
      startTime: options.startTime ?? Date.now(),
      assistantMessageId: options.assistantMessageId,
    };

    const viewCurrent = (): boolean => {
      if (!currentConversationIdRef) return true;
      return opts.conversationId == null || currentConversationIdRef.current === opts.conversationId;
    };

    // The user may have navigated away between deciding to attach and getting
    // here (async gaps in the reattach flow) - don't even open the stream.
    if (!viewCurrent()) return;

    setIsLoading(true);
    // Fresh controller: aborting it stops the LOCAL reader only (server run
    // keeps running - that's the point of fire-and-forget).
    const myController = new AbortController();
    abortControllerRef.current = myController;
    const releaseController = () => {
      if (abortControllerRef.current === myController) abortControllerRef.current = null;
    };
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}/ai/runs/${runId}/stream?after_seq=0`, {
        headers: { 'Authorization': `${tokenType} ${token}` },
        signal: myController.signal
      });
    } catch (error) {
      if (viewCurrent()) setIsLoading(false);
      releaseController();
      if (error instanceof Error && error.name === 'AbortError') return;
      if (!viewCurrent()) return; // background failure: no UI writes into another view
      setMessages(prev => [
        ...prev.slice(0, -1),
        {
          role: 'assistant',
          content: `⚠️ Error: ${(error as Error).message}`,
          model: opts.modelToUse,
          isComplete: true,
          error: true,
          id: 0,
          created_at: new Date().toISOString()
        }
      ]);
      return;
    }

    if (response.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('tokenType');
      navigate('/login');
      setIsLoading(false);
      releaseController();
      return;
    }

    if (!response.ok) {
      if (viewCurrent()) {
        setMessages(prev => [
          ...prev.slice(0, -1),
          {
            role: 'assistant',
            content: '⚠️ An error occurred while processing your request.',
            model: opts.modelToUse,
            isComplete: true,
            error: true,
            id: 0,
            created_at: new Date().toISOString()
          }
        ]);
        setIsLoading(false);
      }
      releaseController();
      return;
    }

    await drainStream(response, opts);
  };

  const handleSubmit = async (
    e: React.FormEvent,
    forcedModel?: Model,
    forcedMessage?: string
  ) => {
    const {
      selectedModel,
      inputMessage,
      isLoading,
      selectedFiles,
      currentConversationId,
      conversations,
      userInfo,
      pendingProjectId,
      abortControllerRef,
      textareaRef,
      chatContainerRef,
      messagesEndRef,
      getModelDisplayName,
      resetTextAreaHeight,
      navigate,
      fetchConversationMessages,
      refreshSharedConversations
    } = dependencies;

    const {
      setInputMessage,
      setIsLoading,
      setSelectedFile,
      setSelectedFileName,
      setSelectedFiles,
      setMessages,
      setCurrentConversationId,
      setConversations,
      setUserInfo,
      setIsFirstMessageLoading,
      setShouldScroll,
      setUploadError,
      clearPendingProject,
      onConversationCreated
    } = callbacks;

    if (e) e.preventDefault();
    const modelToUse = forcedModel ?? selectedModel;
    const messageToUse = forcedMessage ?? inputMessage;

    // Composer attachments (ALL types, including images) now go to the
    // conversation workspace and are sent as a normal message. A files-only
    // send (no typed text) is allowed and uses a default question.
    const hasAttachments = selectedFiles.length > 0;
    if (!forcedModel && ((!messageToUse.trim() && !hasAttachments) || isLoading)) return;
    const outgoingMessage = messageToUse.trim()
      ? messageToUse
      : (hasAttachments ? 'Tolong lihat file yang barusan saya lampirkan.' : messageToUse);

    // Clear any stale composer upload error from a previous attempt.
    setUploadError(null);

    // Add startTime to measure processing time
    const startTime = Date.now();

    // Track message sent
    mixpanel.track('Message Sent', {
      model: modelToUse,
      has_file: selectedFiles.length > 0,
      file_count: selectedFiles.length,
      message_length: messageToUse.length,
      conversation_id: currentConversationId,
      is_first_message: !currentConversationId,
      user_email: userInfo?.email,
      user_status: userInfo?.status,
      timestamp: new Date().toISOString()
    });

    // Increment user message count
    mixpanel.people.increment('Total Messages Sent');

    // Increment model usage counter
    mixpanel.people.increment(`Model: ${modelToUse}`);

    // Track model usage over time
    mixpanel.track('Model Used', {
      model: modelToUse,
      model_display_name: getModelDisplayName(modelToUse),
      conversation_id: currentConversationId,
      user_email: userInfo?.email,
      user_status: userInfo?.status
    });

    abortControllerRef.current = new AbortController();

    // =========================================================================
    // OPTIMISTIC ECHO (2026-08-02) - everything the user can SEE happens at
    // Enter, before any network round trip (conversation create, pending file
    // selections, workspace uploads, /ai/chat/start). The user bubble and the
    // "Berpikir..." placeholder land instantly; every pre-run failure below
    // calls rollbackEcho(), which removes both and puts the text + attachments
    // back in the composer for a retry.
    // =========================================================================
    const savedComposerFiles = selectedFiles;
    const userMessageId = Date.now();
    const assistantPlaceholderId = userMessageId + 1;
    setMessages(prev => [
      ...prev,
      {
        id: userMessageId,
        role: 'user',
        content: outgoingMessage,
        model: modelToUse,
        isComplete: true,
        created_at: new Date().toISOString()
      },
      {
        id: assistantPlaceholderId,
        role: 'assistant',
        content: '',
        model: modelToUse,
        isComplete: false,
        created_at: new Date().toISOString()
      }
    ]);
    setInputMessage('');
    setSelectedFile(null);
    setSelectedFileName(null);
    setSelectedFiles([]);
    setIsLoading(true);
    resetTextAreaHeight();
    setShouldScroll(false);
    const echoFileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (echoFileInput) echoFileInput.value = '';
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 0);

    const rollbackEcho = () => {
      setMessages(prev => prev.filter(
        m => m.id !== userMessageId && m.id !== assistantPlaceholderId
      ));
      setInputMessage(messageToUse);
      setSelectedFiles(savedComposerFiles);
      setSelectedFile(savedComposerFiles[0]?.file ?? null);
      setSelectedFileName(savedComposerFiles[0]?.file.name ?? null);
      setIsLoading(false);
    };

    // Check if this is the first message in the conversation
    const isFirstMessage = !currentConversationId;
    let conversation_id = currentConversationId;

    if (isFirstMessage) {
      setIsFirstMessageLoading(true);
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType');

      try {
        // Get current background and persona values
        const currentBackground = localStorage.getItem('userBackground') || '';
        const currentPersona = localStorage.getItem('assistantPersona') || '';

        const response = await fetch(`${BASE_URL}/conversations/`, {
          method: 'POST',
          headers: {
            'Authorization': `${tokenType} ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: outgoingMessage.length > 30
              ? outgoingMessage.substring(0, 30) + '...'
              : outgoingMessage,
            // Teams phase 3: born inside a project when the user came from
            // "Chat baru di project ini". Omitted entirely otherwise, so a
            // backend without the field sees the exact same body as before.
            ...(pendingProjectId != null ? { project_id: pendingProjectId } : {})
          })
        });

        if (response.ok) {
          const data = await response.json();
          conversation_id = data.id;  // Set this first
          setCurrentConversationId(data.id);
          // Eager ref sync: the stream consumer gates writes on this ref, and
          // events can arrive before the render that mirrors the state change.
          dependencies.currentConversationIdRef.current = data.id;
          localStorage.setItem('lastConversationId', data.id.toString());
          localStorage.setItem('lastConversationUuid', data.uuid);

          // Pilihan file yang dibuat SEBELUM percakapan ada (panel File pada
          // chat baru) diterapkan sekarang, dan DIAWAIT: run pertama membangun
          // manifest dari selections, jadi ini harus selesai sebelum
          // /ai/chat/start di bawah. Gagal memilih satu file tidak boleh
          // menggagalkan chat-nya.
          try {
            const rawPending = JSON.parse(
              localStorage.getItem('pendingFileSelection') || '[]'
            );
            const pending: number[] = Array.isArray(rawPending)
              ? rawPending.filter((n: unknown) => typeof n === 'number')
              : [];
            if (pending.length > 0) {
              await Promise.all(
                pending
                  .slice(0, 20)
                  .map((fid) =>
                    setFileSelection(fid, data.id, true).catch(() => undefined)
                  )
              );
              localStorage.removeItem('pendingFileSelection');
              window.dispatchEvent(new Event('powerai:files-changed'));
            }
          } catch {
            /* jangan pernah blokir pengiriman pesan */
          }

          // this is for the first message to navigate to the new conversation
          navigationData = { id: data.id, uuid: data.uuid };
          window.history.pushState({}, '', `/c/${navigationData.uuid}`);


          // Store background and persona for the new conversation
          const storedSuggestions: StoredSuggestions = JSON.parse(
            localStorage.getItem('conversationSuggestions') || '{}'
          );
          storedSuggestions[data.id] = {
            analysis: '',
            message_count: 0,
            analyzed_at: new Date().toISOString(),
            background: currentBackground,
            persona: currentPersona
          };
          localStorage.setItem('conversationSuggestions', JSON.stringify(storedSuggestions));

          setConversations(prev => [{
            id: data.id,
            uuid: data.uuid,
            title: data.title,
            created_at: data.created_at,
            updated_at: data.updated_at,
            // Present only when the conversation was born inside a project
            // (the create endpoint echoes the assignment and the visibility a
            // team project stamps on it). Carrying them here is what lets the
            // sidebar nest the row under its project on the very first render
            // instead of after the next list refresh.
            ...(typeof data.project_id === 'number' ? { project_id: data.project_id } : {}),
            ...(data.visibility === 'team' || data.visibility === 'private'
              ? { visibility: data.visibility }
              : {})
          }, ...prev]);

          // The project has been consumed by this conversation; the next new
          // chat must start unattached. (A team project makes the conversation
          // born shared server-side - the detail fetch surfaces that.)
          if (pendingProjectId != null) clearPendingProject?.();

          // Hand the freshly created row to the view so the share and project
          // pills reflect it immediately (a team project makes the chat born
          // shared). The run-done hydration confirms it afterwards.
          onConversationCreated?.({
            id: data.id,
            uuid: data.uuid,
            project_id: typeof data.project_id === 'number' ? data.project_id : null,
            visibility: data.visibility === 'team' ? 'team' : 'private'
          });

        }
      } catch (error) {
        console.error('Error creating conversation:', error);
      } finally {
        setIsFirstMessageLoading(false);
      }

      // Covers both the thrown case above and a non-ok create response (which
      // previously fell through with a null id and a hung placeholder).
      if (conversation_id == null) {
        rollbackEcho();
        setUploadError('Gagal memulai percakapan. Periksa koneksi lalu coba lagi.');
        return;
      }
    }

    // Proceed with sending the message

    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType');
    if (!token) {
      navigate('/login');
      return;
    }

    // Handle image generation models differently
    if (isImageGenerationModel(modelToUse)) {
      try {
        // Store the reference images in IndexedDB if exist
        if (selectedFiles.length > 0) {
          for (const fileData of selectedFiles) {
            // Create a copy of the file to ensure it's stored with the correct name
            const referenceFile = new File([fileData.file], fileData.filename, { type: fileData.file.type });
            await storeFile(fileData.filename, referenceFile);
          }
        }

        // Create user message first with files if exist
        const userMessage: MessageWithCitations = {
          id: Date.now(), // Temporary ID
          role: 'user',
          content: selectedFiles.length > 0
            ? `${messageToUse} ${selectedFiles.map(fileData => {
                const sanitizedFilename = sanitizeFilename(fileData.file.name);
                return `[File: ${sanitizedFilename}](file:${fileData.filename})`;
              }).join(' ')}`
            : messageToUse,
          model: modelToUse,
          fileUrl: selectedFiles.length > 0 ? URL.createObjectURL(selectedFiles[0].file) : undefined, // Kept for backward compatibility
          fileUrls: selectedFiles.length > 0 ? selectedFiles.map(fileData => URL.createObjectURL(fileData.file)) : undefined,
          fileData: selectedFiles.length > 0 ? selectedFiles : undefined,
          // Add default values to satisfy the type
          isComplete: true, // User messages are inherently complete
          created_at: new Date().toISOString()
        };

        // Update messages with user message
        // Ensure the object passed matches MessageWithCitations
        setMessages(prev => [...prev, userMessage]);

        // Create initial assistant message with loading state
        const assistantMessage: MessageWithCitations = {
          id: Date.now() + 1, // Temporary ID
          role: 'assistant',
          content: '',
          model: modelToUse,
          isComplete: false,
          fileUrl: 'generating' // Special flag to show loading animation
        };

        // Update messages with loading state
        setMessages(prev => [...prev, assistantMessage]);

        // Reset input
        setInputMessage('');
        setIsLoading(true);
        resetTextAreaHeight();

        // Prepare reference images if any
        let referenceImages: string[] = [];
        if (selectedFiles.length > 0) {
          for (const fileData of selectedFiles) {
            const file = await retrieveFile(fileData.filename);
            if (file) {
              const reader = new FileReader();
              const base64Promise = new Promise<string>((resolve) => {
                reader.onloadend = () => {
                  const base64String = reader.result as string;
                  resolve(base64String.split(',')[1]); // Remove data URL prefix
                };
              });
              reader.readAsDataURL(file);
              referenceImages.push(await base64Promise);
            }
          }
        }

        setTimeout(() => {
          textareaRef.current?.focus();

          setInputMessage('');
          setSelectedFile(null);
          setSelectedFileName(null);
          setSelectedFiles([]);
          setIsLoading(true);
          resetTextAreaHeight();
          setShouldScroll(false);
        }, 100);

        // Make request to image generation API
        let response;
        try {
          response = await fetch(
            `${BASE_URL}/ai/generate/image`,
            {
              method: 'POST',
              headers: {
                'Authorization': `${tokenType} ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                prompt: selectedFiles.length > 0
                  ? `${messageToUse} ${selectedFiles.map(fileData => {
                      const sanitizedFilename = sanitizeFilename(fileData.file.name);
                      return `[File: ${sanitizedFilename}](file:${fileData.filename})`;
                    }).join(' ')}`
                  : messageToUse,
                model: modelToUse,
                conversation_id: conversation_id,
                reference_images: referenceImages.length > 0 ? referenceImages : undefined
              })
            }
          );

          if (!response.ok) {
            let errorMessage = 'An error occurred while generating the image.';

            if (response.status === 403) {
              errorMessage = '⚠️ You do not have permission to generate images. Please check your subscription plan or contact support.';
            } else if (response.status === 429) {
              try {
                const errorData = await response.json();
                errorMessage = creditLimitMessage(errorData.detail);
              } catch {
                errorMessage = '⚠️ Credit limit exceeded. Please upgrade your plan to continue';
              }
            } else if (response.status === 413) {
              errorMessage = 'File size too large. Please upload a smaller file (max 10MB).';
            } else if (response.status === 415) {
              errorMessage = 'Unsupported file type. Please try a different file format.';
            } else if (response.status === 401) {
              localStorage.removeItem('accessToken');
              localStorage.removeItem('tokenType');
              navigate('/login');
              return;
            }

            try {
              const errorData = await response.json();
              if (errorData.detail) {
                errorMessage = errorData.detail;
              }
            } catch { } // Ignore JSON parse errors for error response

            setMessages(prev => {
              const newMessages = [...prev];
              const assistantMessageIndex = newMessages.findIndex(
                msg => !msg.isComplete && msg.role === 'assistant' // Find the loading message
              );

              if (assistantMessageIndex >= 0) {
                // Ensure the updated message includes the ID
                newMessages[assistantMessageIndex] = {
                  ...newMessages[assistantMessageIndex], // Preserve existing fields like ID
                  role: 'assistant',
                  content: errorMessage,
                  model: modelToUse,
                  isComplete: true,
                  error: true,
                  created_at: new Date().toISOString(),
                  // id is preserved by spreading ...newMessages[assistantMessageIndex]
                };
              }

              return newMessages;
            });

            setIsLoading(false);
            return;
          }

          const data = await response.json();

          console.log('Image generation response:', {
            success: data.success,
            model: data.model,
            format: data.format,
            saved_to_file: data.saved_to_file,
            image_data_length: data.image_data ? data.image_data.slice(0, 20) + '...' : 'none'
          });

          if (data.success) {
            // Convert base64 to blob and store in IndexedDB with the saved_to_file as the filename
            const imageBlob = await fetch(`data:image/png;base64,${data.image_data}`).then(r => r.blob());
            const imageFile = new File([imageBlob], data.saved_to_file, { type: 'image/png' });

            // Store the generated image in IndexedDB using the saved_to_file as the key
            await storeFile(data.saved_to_file, imageFile);
            console.log('Generated image stored in IndexedDB with key:', data.saved_to_file);

            // Update last assistant message with the saved_to_file for later retrieval
            setMessages(prev => {
              const newMessages = [...prev];
              const assistantMessageIndex = newMessages.findIndex(
                msg => !msg.isComplete && msg.role === 'assistant' // Find the loading message
              );

              if (assistantMessageIndex >= 0) {
                // Ensure the updated message includes the ID
                newMessages[assistantMessageIndex] = {
                  ...newMessages[assistantMessageIndex], // Preserve existing fields like ID
                  role: 'assistant',
                  content: `Generated image: ${data.saved_to_file}`,
                  model: modelToUse,
                  fileUrl: data.saved_to_file,
                  isComplete: true,
                  created_at: new Date().toISOString(),
                  // id is preserved by spreading ...newMessages[assistantMessageIndex]
                };
              }

              return newMessages;
            });

            // Fetch conversation messages to ensure everything is properly stored and rendered
            if (conversation_id) {
              const conversation = conversations.find(c => c.id === conversation_id);
              if (conversation) {
                const token = localStorage.getItem('accessToken');
                const tokenType = localStorage.getItem('tokenType');
                const params = {
                  conversationId: conversation_id,
                  conversationUuid: conversation.uuid,
                  cursor: null,
                  direction: 'next' as const,
                  pageSize: 50,
                  shouldAutoScroll: false
                };
                const callbacks = {
                  onStart: () => {},
                  onFinish: () => {},
                  onError: (error: any) => {
                    console.error('Error fetching conversation messages:', error);
                  },
                  onUnauthorized: () => {
                    navigate('/login');
                  }
                };
                const result = await fetchConversationMessages(params, token!, tokenType!, callbacks, chatContainerRef);

                // Update messages if successful
                if (result) {
                  const { messages: processedMessages } = result;
                  setMessages(processedMessages);
                }
              }
            }

            // Track successful image generation
            mixpanel.track('Image Generated', {
              model: modelToUse,
              prompt_length: messageToUse.length,
              has_reference_image: selectedFiles.length > 0,
              reference_image_count: selectedFiles.length,
              conversation_id: currentConversationId,
              user_email: userInfo?.email,
              user_status: userInfo?.status
            });

            // Fetch credit info after generation
            const fetchCreditInfo = async () => {
              try {
                const response = await fetch(`${BASE_URL}/auth/credit-info`, {
                  headers: {
                    'Authorization': `${tokenType} ${token}`
                  }
                });

                if (response.ok) {
                  const data = await response.json();
                  setUserInfo(data);
                }
              } catch (error) {
                console.error('Error fetching credit info:', error);
              }
            };

            await fetchCreditInfo();
          }
        } catch (error: any) {
          console.error('Error generating image:', error);
          let errorMessage = 'An error occurred while generating the image.';

          try {
            const errorData = await error.json();
            if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch {
            if (error.message) {
              errorMessage = `Error: ${error.message}`;
            }
          }

          setMessages(prev => {
            const newMessages = [...prev];
            const assistantMessageIndex = newMessages.findIndex(
              msg => !msg.isComplete && msg.role === 'assistant' // Find the loading message
            );

            if (assistantMessageIndex >= 0) {
              // Ensure the updated message includes the ID
              newMessages[assistantMessageIndex] = {
                ...newMessages[assistantMessageIndex], // Preserve existing fields like ID
                role: 'assistant',
                content: `⚠️ ${errorMessage}`,
                model: modelToUse,
                isComplete: true,
                error: true,
                created_at: new Date().toISOString(),
                // id is preserved by spreading ...newMessages[assistantMessageIndex]
              };
            }

            return newMessages;
          });
        } finally {
          setIsLoading(false);
          setSelectedFile(null);
          setSelectedFileName(null);
          setSelectedFiles([]);
        }

        return; // Exit early when handling image generation
      } catch (error: any) {
        console.error('Error generating image:', error);
        let errorMessage = 'An error occurred while generating the image.';

        try {
          const errorData = await error.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          if (error.message) {
            errorMessage = `Error: ${error.message}`;
          }
        }

        setMessages(prev => {
          const newMessages = [...prev];
          const assistantMessageIndex = newMessages.findIndex(
            msg => !msg.isComplete && msg.role === 'assistant' // Find the loading message
          );

          if (assistantMessageIndex >= 0) {
            // Ensure the updated message includes the ID
            newMessages[assistantMessageIndex] = {
              ...newMessages[assistantMessageIndex], // Preserve existing fields like ID
              role: 'assistant',
              content: `⚠️ ${errorMessage}`,
              model: modelToUse,
              isComplete: true,
              error: true,
              created_at: new Date().toISOString(),
              // id is preserved by spreading ...newMessages[assistantMessageIndex]
            };
          }

          return newMessages;
        });
      } finally {
        setIsLoading(false);
        setSelectedFile(null);
        setSelectedFileName(null);
        setSelectedFiles([]);
      }

      return; // Exit early when handling image generation
    }

    // =========================================================================
    // WORKSPACE UPLOAD (2026-07-19) - every composer attachment (all types,
    // including images) now uploads to the server-side conversation workspace
    // instead of the legacy IndexedDB + /analyze/file/stream path. The backend
    // turns workspace files into AUTO context (manifest inline / read_file /
    // view_image tools) and waits for extraction on run start, so upload
    // ACCEPTANCE is enough - we do NOT poll for "ready" before sending. On any
    // failure we roll the optimistic echo back, which restores the user's text
    // + files to the composer, and surface the detail inline for a retry.
    // =========================================================================
    if (hasAttachments) {
      if (conversation_id == null) {
        rollbackEcho();
        setUploadError('Tidak dapat mengunggah file: percakapan belum siap. Coba lagi.');
        return;
      }
      try {
        for (const fileData of savedComposerFiles) {
          await uploadFile(conversation_id, fileData.file);
        }
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          navigate('/login');
          return;
        }
        const detail = err instanceof FileWorkspaceError
          ? err.detail
          : 'Gagal mengunggah file. Periksa koneksi lalu coba lagi.';
        rollbackEcho();
        setUploadError(detail);
        return;
      }
      // Nudge the File panel to refetch immediately (don't wait for its poll).
      window.dispatchEvent(new CustomEvent('powerai:files-changed'));
    }

    // The user bubble and the assistant placeholder are already on screen -
    // the OPTIMISTIC ECHO block appended them at Enter. The placeholder is
    // targeted BY ID (assistantPlaceholderId) by the stream consumer, so it
    // survives any concurrent messages replacement. The optimistic user
    // message stays PLAIN TEXT - composer attachments live in the workspace
    // (right File panel), not inline [File:] markers.

    // =========================================================================
    // LEGACY FILE PATH - dormant since 2026-07-19. Composer attachments now
    // upload to the conversation workspace (see WORKSPACE UPLOAD block above)
    // and are sent as a normal background run; this /analyze/file/stream branch
    // is no longer reached for new sends. Kept intact (NOT deleted) because
    // old-model replay paths share drainStream helpers.
    // =========================================================================
    const USE_LEGACY_FILE_ANALYSIS = false;
    if (USE_LEGACY_FILE_ANALYSIS && selectedFiles.length > 0) {
      const formData = new FormData();
      selectedFiles.forEach(fileData => {
        formData.append('files', fileData.file);
      });
      const fileReferences = selectedFiles.map(fileData => {
        const sanitizedFilename = sanitizeFilename(fileData.file.name);
        return `[File: ${sanitizedFilename}](file:${fileData.filename})`;
      }).join(' ');
      formData.append('question', `${messageToUse} ${fileReferences}`);
      // Always route through the backend 'auto' router.
      formData.append('model', 'auto');
      formData.append('conversation_id', conversation_id?.toString() || '');

      let response: Response;
      try {
        response = await fetch(`${BASE_URL}/ai/analyze/file/stream`, {
          method: 'POST',
          headers: { 'Authorization': `${tokenType} ${token}` },
          body: formData,
          signal: abortControllerRef.current?.signal
        });
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          setIsLoading(false);
          return;
        }
        setMessages(prev => [
          ...prev.slice(0, -1),
          {
            role: 'assistant',
            content: `⚠️ Error: ${(error as Error).message}`,
            model: modelToUse,
            isComplete: true,
            error: true,
            id: 0,
            created_at: new Date().toISOString()
          }
        ]);
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        let errorMessage = 'An error occurred. Please try again.';

        if (response.status === 429) {
          try {
            const errorData = await response.json();
            const message = creditLimitMessage(errorData.detail);
            setMessages(prev => {
              const cleanMessages = prev.filter(m => m.isComplete !== false && !m.error);
              return [...cleanMessages, {
                role: 'assistant',
                content: message,
                model: modelToUse,
                isComplete: true,
                error: true,
                id: 0,
                created_at: new Date().toISOString()
              }];
            });
            setIsLoading(false);
            return;
          } catch {
            // Fall through to default error handling
          }
        } else if (response.status === 413) {
          errorMessage = 'File size too large. Please upload a smaller file (max 10MB).';
        } else if (response.status === 415) {
          errorMessage = 'Unsupported file type. Please try a different file format.';
        } else if (response.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('tokenType');
          navigate('/login');
          return;
        }

        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch { } // Ignore JSON parse errors for error response

        setMessages(prev => [
          ...prev.slice(0, -1),
          {
            role: 'assistant',
            content: `⚠️ ${errorMessage}`,
            model: modelToUse,
            isComplete: true,
            error: true,
            id: 0,
            created_at: new Date().toISOString()
          }
        ]);
        setIsLoading(false);
        return;
      }

      // Drain the file-analysis SSE stream through the shared consumer.
      await drainStream(response, {
        isReattach: false,
        conversationId: conversation_id,
        conversationUuid: isFirstMessage ? undefined : conversations.find(c => c.id === conversation_id)?.uuid,
        modelToUse,
        isFirstMessage,
        navigationData,
        startTime,
        assistantMessageId: assistantPlaceholderId
      });
      return;
    }

    // =========================================================================
    // TEXT PATH - fire-and-forget. POST /ai/chat/start to spawn the run, store
    // the run_id under activeRun:{conversation_id}, then attach to its stream.
    // If the tab is closed/refreshed mid-run, Chat.tsx re-attaches on reopen.
    // =========================================================================
    const startBody = JSON.stringify({
      message: outgoingMessage,
      // Always route through the backend 'auto' router.
      model: 'auto',
      conversation_id: conversation_id,
      background: localStorage.getItem('userBackground') || '',
      assistant_persona: localStorage.getItem('assistantPersona') || ''
    });

    try {
      const startResp = await fetch(`${BASE_URL}/ai/chat/start`, {
        method: 'POST',
        headers: {
          'Authorization': `${tokenType} ${token}`,
          'Content-Type': 'application/json'
        },
        body: startBody,
        signal: abortControllerRef.current?.signal
      });

      // 409 = a run is already active for this conversation (e.g. started in
      // another tab). Attach to it instead of erroring.
      if (startResp.status === 409) {
        const activeRunId = await queryActiveRunId(conversation_id);
        if (activeRunId) {
          if (conversation_id != null) {
            localStorage.setItem(`activeRun:${conversation_id}`, activeRunId);
            setConversations(prev => prev.map(c =>
              c.id === conversation_id ? { ...c, active_run_status: 'running' as const } : c
            ));
            // The "Tim" block has no optimistic path - refetch it so a shared
            // conversation picks the run up now instead of on a later poll.
            refreshSharedConversations?.();
          }
          await consumeRunStream(activeRunId, {
            isReattach: true,
            conversationId: conversation_id,
            conversationUuid: conversations.find(c => c.id === conversation_id)?.uuid,
            modelToUse,
            isFirstMessage,
            navigationData,
            startTime,
            assistantMessageId: assistantPlaceholderId
          });
        } else {
          // Run finished between the 409 and our lookup - reconcile via refetch.
          setIsLoading(false);
          const conv = conversations.find(c => c.id === conversation_id);
          if (conv && conversation_id != null) {
            const result = await fetchConversationMessages(
              {
                conversationId: conversation_id,
                conversationUuid: conv.uuid,
                cursor: null,
                direction: 'next' as const,
                pageSize: 50,
                shouldAutoScroll: false
              },
              token!, tokenType!,
              {
                onStart: () => {},
                onFinish: () => {},
                onError: (error: any) => { console.error('Error fetching conversation messages:', error); },
                onUnauthorized: () => { navigate('/login'); }
              },
              chatContainerRef
            );
            if (result) setMessages(result.messages);
          } else {
            // No conversation to refetch - drop the dangling placeholder.
            setMessages(prev => prev.slice(0, -1));
          }
        }
        return;
      }

      if (startResp.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('tokenType');
        navigate('/login');
        return;
      }

      if (!startResp.ok) {
        let errorMessage = 'An error occurred. Please try again.';

        if (startResp.status === 429) {
          try {
            const errorData = await startResp.json();
            const message = creditLimitMessage(errorData.detail);
            setMessages(prev => {
              const cleanMessages = prev.filter(m => m.isComplete !== false && !m.error);
              return [...cleanMessages, {
                role: 'assistant',
                content: message,
                model: modelToUse,
                isComplete: true,
                error: true,
                id: 0,
                created_at: new Date().toISOString()
              }];
            });
            setIsLoading(false);
            return;
          } catch {
            // Fall through to default error handling
          }
        } else if (startResp.status === 413) {
          errorMessage = 'File size too large. Please upload a smaller file (max 10MB).';
        } else if (startResp.status === 415) {
          errorMessage = 'Unsupported file type. Please try a different file format.';
        }

        try {
          const errorData = await startResp.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch { } // Ignore JSON parse errors for error response

        setMessages(prev => [
          ...prev.slice(0, -1),
          {
            role: 'assistant',
            content: `⚠️ ${errorMessage}`,
            model: modelToUse,
            isComplete: true,
            error: true,
            id: 0,
            created_at: new Date().toISOString()
          }
        ]);
        setIsLoading(false);
        return;
      }

      const startData = await startResp.json();
      const runId: string | undefined = startData?.run_id;
      const resolvedConvId: number | null =
        typeof startData?.conversation_id === 'number' ? startData.conversation_id : conversation_id;

      if (!runId) {
        throw new Error('No run_id returned from /ai/chat/start');
      }

      if (resolvedConvId != null) {
        localStorage.setItem(`activeRun:${resolvedConvId}`, runId);
        // Optimistic sidebar stamp: mark this conversation as running NOW so
        // the indicator (and the status poll) doesn't wait for the next
        // server list refresh.
        setConversations(prev => prev.map(c =>
          c.id === resolvedConvId ? { ...c, active_run_status: 'running' as const } : c
        ));
        // Same for the shared list, which only ever learns about runs from the
        // server. Pairing it with the optimistic stamp keeps both sidebar
        // blocks on the same lifecycle instead of leaving the Tim block to the
        // poll, which is what let a mid-run snapshot go stale forever.
        refreshSharedConversations?.();
      }

      await consumeRunStream(runId, {
        isReattach: false,
        conversationId: resolvedConvId,
        // First message: leave uuid unset so drainStream's conversation refetch
        // falls back to the (stale) list and skips - the first-message branch
        // already refetches, matching pre-refactor behaviour.
        conversationUuid: isFirstMessage ? undefined : conversations.find(c => c.id === resolvedConvId)?.uuid,
        modelToUse,
        isFirstMessage,
        navigationData,
        startTime,
        assistantMessageId: assistantPlaceholderId
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // POST aborted (user left before the run started). The server may still
        // spawn the run; reopening the conversation re-attaches via active-run.
        console.log('chat/start request aborted');
        setIsLoading(false);
        return;
      }
      const errorMessage = error instanceof Error
        ? `Error: ${error.message}`
        : 'An error occurred while processing your request.';
      setMessages(prev => [
        ...prev.slice(0, -1),
        {
          role: 'assistant',
          content: `⚠️ ${errorMessage}`,
          model: modelToUse,
          isComplete: true,
          error: true,
          id: 0,
          created_at: new Date().toISOString()
        }
      ]);
      setIsLoading(false);
    }
  };

  return { handleSubmit, consumeRunStream };
}
